import React from "react";

import { useAuth } from "@wc/domain/authn";

import { loadTeam } from "../core/team-access";
import { updateTeam } from "../core/team-update";

/**
 * @param {object} team The team or null
 * @param {(team: object|null) => void} setTeam To set the team
 * @param {boolean} loading Whether the team is loading or not
 * @param {object} authData The auth context data
 * @returns {object} The team context data
 */
function createTeamContextData(team, setTeam, loading, authData) {
  return {
    team,
    isLoading: loading,
    updateTeamName: async (newName) => {
      if (newName.length === 0) {
        throw new Error("Unable to set an empty name");
      }
      const oldName = team.name;

      const newTeam = { ...team };
      newTeam.name = newName;
      setTeam(newTeam);
      try {
        await updateTeam(newTeam, authData.getCurrentUser());
      } catch (error) {
        console.error(error);
        const backToOldName = { ...team };
        backToOldName.name = oldName;
        setTeam(backToOldName);
      }
    },
  };
}

export const TeamContext = React.createContext(null);

/**
 * @param {object} props The provider props
 * @param {string} props.teamId The id of the team
 * @param {string} props.userId The id of the user looking for this
 * @param {React.ReactElement} props.children The children to render
 * @returns {React.ReactElement} The team provider
 */
export default function TeamProvider({ teamId, userId, children }) {
  const authData = useAuth();
  const [team, setTeam] = React.useState(null);

  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const fetchTeam = async () => {
      try {
        const loadedTeam = await loadTeam(teamId, userId);
        setTeam(loadedTeam);
        setLoading(false);
      } catch (error) {
        console.error(`Could not load team ${teamId}`, error);
        setTeam(null);
        setLoading(false);
      }
    };
    if (!team) {
      fetchTeam();
    }
  }, [teamId, userId, team]);

  const contextData = createTeamContextData(team, setTeam, loading, authData);

  return (
    <TeamContext.Provider value={contextData}>{children}</TeamContext.Provider>
  );
}
